import { fetchInsightsWebshop } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { funnelRows } from '../../../core/rules/insights/funnel';
import { share } from '../../../core/rules/insights/stats';
import { ChartFrame } from '../charts/ChartFrame';
import { BarList } from '../charts/BarList';
import { FunnelChart } from '../charts/FunnelChart';
import { IntegrationEmpty } from '../IntegrationEmpty';
import { Kpi } from '../Kpi';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt, ratio } from '../insightFormat';
import { useAdmin } from '../../AdminContext';
import { stopReasonLabel } from '../../../core/rules/stopReasons';
import type { PeriodState } from '../../sales/usePeriodParams';

/** Webshop: wat verkoopt, wat samen gaat, waar mensen afhaken, abonnement en cadeaubonnen. */
export function WebshopTab({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsWebshop, period.from, period.to);
  // De tekst per reden zoals Sarah hem in Paginateksten zette (en de klant hem zag).
  const {
    pages
  } = useAdmin();
  const span = `${period.from}-${period.to}`;
  return <ReportView report={report} what="De webshopcijfers" data-id="element-2235" data-mp="35aqdou4nr2o3">
      {w => {
      const funnel = w.funnel ? funnelRows(w.funnel) : null;
      return <>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]" data-id="element-2236" data-mp="2vcsuil1nnw2c">
              {funnel ? <ChartFrame title="Van bekijken tot betalen" description="Waar bezoekers afhaken" fileName={`trechter-${span}`} table={{
            columns: [{
              label: 'Stap'
            }, {
              label: 'Aantal'
            }, {
              label: 'Door van vorige stap',
              kind: 'pct'
            }, {
              label: 'Van het begin',
              kind: 'pct'
            }],
            rows: funnel.map(r => [r.label, r.count, r.fromPrevious, r.fromStart])
          }} data-id="element-2237" data-mp="2rb7if1wy3lt7">
                  <FunnelChart rows={funnel} data-id="element-2238" data-mp="23m4uxdfmqyqs" />
                  {funnel.some(r => r.mixed) && <p className="mt-4 text-[12px] leading-relaxed text-muted" data-id="element-2239" data-mp="1sy6isr01koub">
                      * Betaald komt uit je eigen bestellingen; de stappen ervoor uit de bezoekersstatistiek, die op de betaalpagina’s niet meet. Het percentage is een benadering.
                    </p>}
                </ChartFrame> : <IntegrationEmpty id="umami" compact data-id="element-2240" data-mp="2cnz3bq0imwby" />}

              <ChartFrame title="Producten" description="Omzet excl. btw, en hoeveel kopers het nog eens kochten" tableOnly fileName={`producten-${span}`} table={{
            columns: [{
              label: 'Product'
            }, {
              label: 'Omzet',
              kind: 'money'
            }, {
              label: 'Kopers'
            }, {
              label: 'Kocht opnieuw',
              kind: 'pct'
            }],
            rows: w.products.map(p => [p.name, p.revenueCents, p.buyers, share(p.repeatBuyers, p.buyers)])
          }} data-id="element-2241" data-mp="2cpto94bseog1" />
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-id="element-2242" data-mp="39r1ypiwm2fqq">
              <Panel title="Vaak samen gekocht" description="In dezelfde bestelling, minstens 3 keer" data-id="element-2243" data-mp="35r1jhw1ou0im">
                {w.pairs.length === 0 ? <p className="text-[13px] text-muted" data-id="element-2244" data-mp="3jhenjnbgkv2o">Nog geen vaste combinaties.</p> : <ul className="divide-y divide-line text-[13px]" data-id="element-2245" data-mp="3ejliwypt094i">
                    {w.pairs.map(p => <li key={`${p.a}-${p.b}`} className="flex items-baseline justify-between gap-3 py-2" data-id="element-2246" data-mp="2qv2dqs21wcgn">
                        <span className="min-w-0 text-ink" data-id="element-2247" data-mp="30rf53ja4ie88">
                          {p.a} <span className="text-muted" data-id="element-2248" data-mp="30sqa1mfb1s0x">+</span> {p.b}
                        </span>
                        <span className="shrink-0 tabular-nums text-muted" data-id="element-2249" data-mp="2vr0vvxwscy8m">{p.count}×</span>
                      </li>)}
                  </ul>}
                <p className="mt-3 text-[12px] text-muted" data-id="element-2250" data-mp="b1bi7o7tjihi">Een idee voor een routine of een set.</p>
              </Panel>

              <Panel title="Uit het huidadvies" description="Producten die vanuit het advies of een routine in de mand kwamen" data-id="element-2251" data-mp="2gs83wngvio9f">
                <dl className="grid grid-cols-2 gap-4" data-id="element-2252" data-mp="2qokz5ps2ha6a">
                  <Kpi label="Omzet" value={w.advice.revenueCents} kind="money" data-id="element-2253" data-mp="2qn03fwu0nl6u" />
                  <div className="flex flex-col" data-id="element-2254" data-mp="2ur7s5u0h5152">
                    <dt className="text-[12px] text-muted" data-id="element-2255" data-mp="2unttavr0ra2d">Bestellingen</dt>
                    <dd className="mt-1 text-[18px] tabular-nums text-ink" data-id="element-2256" data-mp="2zozmeuolczcf">{fmt(w.advice.orders)}</dd>
                  </div>
                </dl>
              </Panel>

              <Panel title="Cadeaubonnen" data-id="element-2257" data-mp="39g3ynmmvu63k">
                <dl className="space-y-2 text-[14px]" data-id="element-2258" data-mp="39h5lsph6aj1n">
                  {([['Verkocht', w.vouchers.issuedCents], ['Ingewisseld', w.vouchers.redeemedCents], ['Nog open (alle bonnen)', w.vouchers.outstandingCents]] as const).map(([label, cents]) => <div key={label} className="flex items-baseline justify-between gap-4" data-id="element-2259" data-mp="1bss26yv3by0u">
                      <dt className="text-muted" data-id="element-2260" data-mp="16qucpmw3k6zg">{label}</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2261" data-mp="12qafkjbix3db">{fmt(cents, 'money')}</dd>
                    </div>)}
                </dl>
              </Panel>
            </div>

            <Panel title="Bewust Abonnement" data-id="element-2262" data-mp="1uhqar1r5byf5">
              <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]" data-id="element-2263" data-mp="1uhgkre63pr9g">
                <dl className="grid grid-cols-2 gap-x-5 gap-y-4" data-id="element-2264" data-mp="1qho5kim7cmv4">
                  {([['Actief', w.subscriptions.active], ['Nieuw', w.subscriptions.started], ['Opgezegd', w.subscriptions.cancelled], ['Incasso mislukt', w.subscriptions.failedPayments]] as const).map(([label, n]) => <div key={label} className="flex flex-col" data-id="element-2265" data-mp="29de94a9s9rb4">
                      <dt className="text-[12px] text-muted" data-id="element-2266" data-mp="2sc11w89aqw13">{label}</dt>
                      <dd className={`mt-1 text-[18px] tabular-nums ${label === 'Incasso mislukt' && n > 0 ? 'text-clay' : 'text-ink'}`} data-id="element-2267" data-mp="2xau61a2p1aol">{fmt(n)}</dd>
                    </div>)}
                </dl>
                <div data-id="element-2268" data-mp="2njgatehryx19">
                  <h3 className="mb-3 text-[13px] text-muted" data-id="element-2269" data-mp="2niy75wss9gjb">Waarom opgezegd</h3>
                  <BarList empty="Niemand opgezegd in deze periode." rows={w.subscriptions.reasons.map(r => ({
                key: r.code ?? 'geen',
                label: r.code ? stopReasonLabel(r.code, pages.portal.stopReasons) : 'Geen reden gegeven',
                value: r.count,
                display: String(r.count),
                meta: ratio(r.count, w.subscriptions.cancelled)
              }))} data-id="element-2270" data-mp="29k8502r3wqo3" />
                </div>
              </div>
            </Panel>
          </>;
    }}
    </ReportView>;
}